<template>
  <div style="display: contents;">
    <KvModal v-if="$pointsProgram()" size="small" ref="pointsModal">
      <template #title>{{ t('clients-modals-manage_points') }}</template>
      <template #body>
        <div class="d-flex flex-column align-items-center gap-md">
          <div class="d-flex align-items-center gap-sm">
            <KvTypography size="large" color="primary">
              {{ t('clients-modals-current_value') }}
            </KvTypography>
            <KvChip density="compact" rounded="soft">{{ currentPoints }}{{ t('clients-detail-point_plural') }}</KvChip>
          </div>

          <div class="d-flex align-items-center gap-sm">
            <KvActionSwitch v-model="changePoint.active" true-color="#17B26A" false-color="#F04438" />
            <KvInput v-model="changePoint.amount" type="number" :min="1" />
          </div>
          <div class="d-flex align-items-center gap-sm">
            <KvTypography size="large" color="primary">
              {{ t('clients-modals-changed_value') }}
            </KvTypography>
            <KvChip density="compact" rounded="soft" :color="calculatePoints >= 0 ? 'success' : 'error'">{{ calculatePoints }}{{ t('clients-detail-point_plural') }}</KvChip>
          </div>
        </div>
      </template>

      <template #footer="{ toggle }">
        <div class="d-flex align-items-center w-100 justify-content-end gap-sm">
          <KvButton variant="outlined" @click="toggle">{{ t('clients-modals-close') }}</KvButton>
          <KvButton variant="filled" prepend-icon="ui-check" @click="applyPoints(toggle)" :loading="processing.points">{{ t('clients-modals-apply') }}</KvButton>
        </div>
      </template>
    </KvModal>

    <KvModal size="small" ref="formsModal">
      <template #title v-if="!formLink">{{ t('clients-modals-create_single_form_link') }}</template>
      <template #title v-else>
        <KvTypography color="success" size="huge">✓</KvTypography>
        <div>{{ t('clients-modals-generated_link') }}</div>
      </template>
      <template #body>
        <div class="d-flex flex-column gap-sm" v-if="!formLink">
          <KvTypography colo="primary" weight="medium" size="medium">{{ t('clients-modals-form') }}</KvTypography>
          <KvSelect size="large" v-model="formId" :load-options="searchForms" value-field="id" label-field="name" :placeholder="t('clients-modals-select_form')" lazy full-width />
        </div>
        <div class="d-flex gap-sm" v-else>
          <KvInput class="flex-1" readonly colo="primary" weight="medium" size="medium" :value="formLink" />
          <KvCopyButton :value="formLink" />
        </div>
      </template>

      <template #footer="{ toggle }">
        <div class="d-flex align-items-center w-100 justify-content-end gap-sm">
          <KvButton variant="outlined" @click="toggle(); formLink = null">{{ t('clients-modals-close') }}</KvButton>
          <KvButton v-if="!formLink" variant="filled" prepend-icon="ui-plus" :disabled="!formId" @click="generateLink()" :loading="processing.forms">{{ t('clients-modals-generate_link') }}</KvButton>
        </div>
      </template>
    </KvModal>

    <create-task-modal
      ref="createTaskModal"
      @saved="onCreateTaskSaved"
    />

    <ClientFormModal ref="clientFormModal" :clientId="clientId" :client="client" @client-created="$emit('client-created', $event)" @refresh-client="$emit('refresh-client', $event)" />
    <KvModal ref="addOrder" :startsOpen="false" id="addOrder" @hide="() => { }" v-bind:class="{ 'modal-wide': false }">
      <template slot="title"> {{ t('clients-basic-include_orders') }} </template>
      <template #body="{ toggle }">
        <AddOrder @orderSaved="afterNewOrder($event)" @cancel="toggle()" :default-phone="client.whatsapp" :show-title="false" />
      </template>
    </KvModal>

    <KvModal size="medium" ref="deleteClientModal" @hide="isCorrectDelete = ''">
      <template #title>
        <span>{{ t('clients-modals-delete_client_title') }}</span>
      </template>
      <template #body>
        <div class="d-flex flex-column gap-sm">
            <KvTypography weight="medium" size="large" color="error">
                {{ t('clients-modals-delete_info_irreversible') }}
            </KvTypography>
            
            <KvTypography size="medium" color="primary">
                {{ t('clients-modals-delete_info_will_delete') }}
            </KvTypography>
            
            <ul class="pl-4 m-0" style="color: #1f2a37; font-size: 14px;">
                <li>{{ t('clients-modals-delete_info_all_sales') }}</li>
                <li>{{ t('clients-modals-delete_info_all_tasks') }}</li>
                <li>{{ t('clients-modals-delete_info_all_messages') }}</li>
                <li>{{ t('clients-modals-delete_info_all_records') }}</li>
            </ul>
            
            <KvTypography size="medium" color="error" weight="medium">
                {{ t('clients-modals-delete_info_permanent') }}
            </KvTypography>

            <KvTypography size="medium" color="primary">
                {{ t('clients-modals-delete_info_inconsistency') }}{{ t('clients-modals-delete_info_inconsistency_detail') }}
            </KvTypography>
        </div>

        <div class="mt-4">
          <KvTypography size="medium" color="primary" class="mb-2">
            {{ t('confirm_phrase-instruction') }}
          </KvTypography>
          <div class="text-uppercase mb-3" style="font-size: 15px; letter-spacing: 0.5px;">
            <span v-for="(char, index) in deletePlaceholderText" :key="index" :style="{ color: isCorrectDelete && isCorrectDelete[index] && isCorrectDelete[index].toLowerCase() === char.toLowerCase() ? '#F04438' : '#1f2a37' }">
              <strong v-if="char != ' '">{{ char }}</strong>
              <strong v-else class="mx-1"></strong>
            </span>
          </div>
          <KvInput v-model="isCorrectDelete" :placeholder="t('confirm_phrase-placeholder')" />
        </div>
      </template>
      <template #footer="{ toggle }">
        <div class="d-flex align-items-center w-100 justify-content-end gap-sm">
          <KvButton variant="outlined" @click="toggle">{{ t('clients-modals-cancel') }}</KvButton>
          <KvButton variant="filled" class="btn-danger-filled" prepend-icon="ui-trash-01" :loading="processing.deleteClient" :disabled="!isCorrectDelete || isCorrectDelete.toLowerCase() !== deletePlaceholderText.toLowerCase()" @click="deleteClient(toggle)">{{ t('clients-modals-delete_client_button') }}</KvButton>
        </div>
      </template>
    </KvModal>
  </div>
</template>

<style lang="less">
.delete-modal-description {
  margin-top: 8px;
}

.fields-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  // gap: 12px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}
</style>

<script>
import httpStatus from "@/helpers/httpStatus";
import keviApi from "@/keviApi";
import KvModal from "@/components/modal/modal.vue";
import KvActionSwitch from "./kv-action-switch.vue"; // Ajuste o caminho se necessário
import KvChip from "@/components/ui/kv-chip.vue";
import KvTypography from "@/components/ui/kv-typography.vue";
import KvInput from "@/components/inputs/kv-input.vue";
import KvSelect from "@/components/inputs/kv-select.vue";
import KvDatePicker from "@/components/inputs/kv-date-picker.vue";
import KvButton from "@/components/form/kv-button.vue";
import KvCopyButton from "@/components/form/kv-copy-button.vue";
import AddOrder from "@/dashboard/add-order/add-order.vue";
import ClientFormModal from "./client-form-modal.vue";
import KvPhoneInput from "@/components/kv-phone-input/kv-phone-input.vue";
import CreateTaskModal from "@/tasks/create-task-modal.vue";
import phoneMixin from "@/mixins/phone-mixin";

export default {
  name: "ClientModals",
  mixins: [phoneMixin],
  components: {
    KvPhoneInput,
    KvModal,
    KvActionSwitch,
    KvChip,
    KvTypography,
    KvInput,
    KvSelect,
    KvButton,
    KvCopyButton,
    KvDatePicker,
    AddOrder,
    ClientFormModal,
    CreateTaskModal,
  },
  props: {
    clientId: { type: [String, Number], required: true },
    client: { type: Object, default: () => ({}) },
    insights: { type: Object, default: () => ({}) },
    groups: { type: Array, default: [] },
    orderFields: { type: Array, default: [] },
    loadTimeline: Function,
  },
  data() {
    return {
      changePoint: {
        amount: 1,
        active: true,
      },
      processing: {
        points: false,
        forms: false,
        deleteClient: false,
      },
      formId: null,
      formLink: null,
      isCorrectDelete: "",
      order: {},
      customGroups: []
    };
  },
  computed: {
    money() {
      return {
        decimal: ",",
        thousands: ".",
        prefix: this.currencySymbol + " ",
        suffix: "",
        precision: 2,
        masked: false,
      };
    },
    currentPoints() {
      return this.insights?.rewards_report?.available_points || 0;
    },
    calculatePoints() {
      const current = this.currentPoints;
      return this.changePoint.active
        ? current + Number(this.changePoint.amount)
        : current - Number(this.changePoint.amount);
    },
    calculateAmount() {
      return this.changePoint.active ? Number(this.changePoint.amount) : Number(this.changePoint.amount) * -1;
    },
    clientDisplayName() {
      return [this.client?.first_name, this.client?.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
    },
    deletePlaceholderText() {
      const firstName = this.client?.first_name || '';
      return this.t('clients-modals-delete_info_confirm_phrase_placeholder', [firstName.toUpperCase()]);
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    // Métodos Públicos para o Pai chamar
    openPoints() {
      if (!this.$refs.pointsModal) {
        return;
      }
      this.$refs.pointsModal.toggleShow();
    },
    openForms() {
      this.$refs.formsModal.toggleShow();
    },
    openTasks() {
      if (!this.$refs.createTaskModal) {
        return;
      }
      this.$refs.createTaskModal.open({
        modalTitle: this.t("clients-modals-create_task_title"),
        clients_id: this.clientId,
        lockClient: true,
        lockedClientLabel: this.clientDisplayName,
      });
    },
    onCreateTaskSaved() {
      this.$emit("refresh-tasks");
    },
    openClient() {
      this.$refs.clientFormModal.openClient();
    },
    async openCreateClient() {
      this.$refs.clientFormModal.openCreateClient();
    },
    openOrder() {
      this.$refs.addOrder.toggleShow();
    },
    openDeleteClient() {
      this.$refs.deleteClientModal.toggleShow();
    },
    async deleteClient(toggle) {
      this.processing.deleteClient = true;
      try {
        const options = keviApi.getOptions(
          `place/client/${this.clientId}`,
          "DELETE",
        );
        const response = await this.$http(options);
        if (response.status === httpStatus.OK) {
          this.$store.dispatch(
            "notification/success",
            this.t('clients-detail-client_deleted_success'),
          );
          toggle();
          this.$router.push("/clients");
        } else {
          this.$store.dispatch(
            "notification/warning",
            this.t('clients-detail-client_delete_error'),
          );
        }
      } catch {
        this.$store.dispatch(
          "notification/warning",
          this.t('clients-detail-client_delete_error'),
        );
      } finally {
        this.processing.deleteClient = false;
      }
    },
    afterNewOrder() {
      this.loadTimeline();
      this.$refs.addOrder.close();
    },

    // Lógica Interna
    async searchForms() {
      const response = await this.$http(keviApi.getOptions(`forms`));
      if (response.status === httpStatus.OK) {
        return response.body;
      }
    },
    async generateLink() {
      this.processing.forms = true;
      try {
        const options = keviApi.getOptions(`form/${this.formId}/generate-link`, "POST", {
          clientId: this.clientId,
        });
        const response = await this.$http(options);

        if (response.status === httpStatus.CREATED) {
          this.$emit("refresh-forms"); // Avisa o pai para atualizar
          this.formId = null;
          this.formLink = response.body.url;
        }
      } finally {
        this.processing.forms = false;
      }
    },
    async applyPoints(toggle) {
      if (this.calculateAmount === 0) return;

      this.processing.points = true;
      try {
        const options = keviApi.getOptions(`client/${this.clientId}/points`, "POST", {
          amount: this.calculateAmount,
        });
        const response = await this.$http(options);

        if (response.status === httpStatus.CREATED) {
          this.$emit("refresh-rewards"); // Avisa o pai para atualizar
          this.changePoint = { amount: 1, active: true };
          toggle();
        }
      } finally {
        this.processing.points = false;
      }
    },
  }
};
</script>
